import type { TextUnit } from "@models/batch";

/**
 * Parse a CSV file into text units.
 * Expected format:
 *   text_id,text
 *   001,"Hello world"
 *   002,"Good morning"
 *
 * Also supports tab-separated (TSV) and headerless files
 * (auto-generates IDs).
 */
export function parseCsvToTextUnits(content: string): TextUnit[] {
  const lines = content
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) return [];

  // Detect separator
  const firstLine = lines[0];
  const separator = firstLine.includes("\t") ? "\t" : ",";

  // Check if first line looks like a header
  const hasHeader = /^(text_id|id)/i.test(firstLine);
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const units: TextUnit[] = [];

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];
    const sepIdx = line.indexOf(separator);

    if (sepIdx === -1) {
      // Single column — treat entire line as text, auto-generate ID
      units.push({ text_id: String(i + 1).padStart(4, "0"), text: unquote(line) });
    } else {
      const id = unquote(line.slice(0, sepIdx));
      const text = unquote(line.slice(sepIdx + 1));
      if (text) {
        units.push({ text_id: id || String(i + 1).padStart(4, "0"), text });
      }
    }
  }

  return units;
}

/**
 * Parse a JSON file into text units.
 * Supports:
 * - Array of {text_id, text}
 * - Array of strings (auto-generates IDs)
 * - Object with {key: "text"} pairs (key becomes text_id)
 */
export function parseJsonToTextUnits(content: string): TextUnit[] {
  const data = JSON.parse(content);

  if (Array.isArray(data)) {
    return data.map((item, i) => {
      if (typeof item === "string") {
        return { text_id: String(i + 1).padStart(4, "0"), text: item };
      }
      if (typeof item === "object" && item !== null) {
        return {
          text_id: item.text_id ?? item.id ?? String(i + 1).padStart(4, "0"),
          text: item.text ?? item.source_text ?? item.content ?? "",
        };
      }
      return { text_id: String(i + 1).padStart(4, "0"), text: String(item) };
    }).filter((u) => u.text.length > 0);
  }

  if (typeof data === "object" && data !== null) {
    return Object.entries(data)
      .map(([key, value]) => ({
        text_id: key,
        text: typeof value === "string" ? value : JSON.stringify(value),
      }))
      .filter((u) => u.text.length > 0);
  }

  return [];
}

/**
 * Parse an uploaded file into text units based on its extension.
 */
export async function parseFileToTextUnits(file: File): Promise<TextUnit[]> {
  const content = await file.text();
  const ext = file.name.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "json":
    case "jsonl":
      if (ext === "jsonl") {
        // JSONL: each line is a JSON object
        const lines = content.split(/\r?\n/).filter((l) => l.trim());
        return lines
          .map((line, i) => {
            try {
              const obj = JSON.parse(line);
              return {
                text_id: obj.text_id ?? obj.id ?? String(i + 1).padStart(4, "0"),
                text: obj.text ?? obj.source_text ?? "",
              };
            } catch {
              return { text_id: String(i + 1).padStart(4, "0"), text: "" };
            }
          })
          .filter((u) => u.text.length > 0);
      }
      return parseJsonToTextUnits(content);

    case "csv":
    case "tsv":
    case "txt":
    default:
      return parseCsvToTextUnits(content);
  }
}

function unquote(s: string): string {
  const trimmed = s.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}
